import type { Dispatch, ReactNode, SetStateAction } from 'react';
import { createContext, useContext, useState } from 'react';

interface HighlightState {
	highlightedText: string;
	setHighlightedText: Dispatch<SetStateAction<string>>;
}

const HighlightContext = createContext<HighlightState | null>({
	highlightedText: '',
	setHighlightedText: () => undefined,
});

// Context used for data shared across all tables
export const useHighlightMeta = (): HighlightState => {
	const context = useContext(HighlightContext);
	if (context === null) {
		throw new Error(
			'Could not get highlight context. Did you forget to use HighlightMetaProvider?'
		);
	}

	return context;
};

/** Same as {@link useHighlightMeta}, but will never throw errors. */
export const useHighlightMetaSafe = (): HighlightState | null => {
	return useContext(HighlightContext);
};

interface HighlightMetaProviderProps {
	children: ReactNode;
}

export function HighlightMetaProvider({
	children,
}: HighlightMetaProviderProps) {
	const [highlightedText, setHighlightedText] = useState<string>('');
	const value: HighlightState = { highlightedText, setHighlightedText };
	return (
		<HighlightContext.Provider value={value}>
			{children}
		</HighlightContext.Provider>
	);
}
